import {AgentsNotSupportedDialog, NoAgentsAvailableDialog} from '@github-ui/copilot-chat/components/AgentsDialogs'
import {KnowledgeSelectPanel} from '@github-ui/copilot-chat/components/KnowledgeSelectPanel'
import {ReferencesSelectPanel} from '@github-ui/copilot-chat/components/ReferencesSelectPanel'
import {useChatState, useChatStateValues} from '@github-ui/copilot-chat/CopilotChatContext'
import {useInsertAgent, type UseInsertAgentProps} from '@github-ui/copilot-chat/utils/agents-helpers'
import {isRepository} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {BookIcon, FileCodeIcon, ImageIcon, RepoIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {type RefObject, useRef} from 'react'

import {CopilotImageAttacher} from '../utils/copilot-image-attacher'
import {useChatAutocomplete} from '../utils/CopilotChatAutocompleteContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {AgentMenu} from './AgentMenu'
import type {AttachmentMenuPanelType} from './AttachmentMenuPanelType'
import CopilotImageHiddenComponent from './CopilotImageHiddenComponent'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from './PortalContainerUtils'
import {RepoSelectPanel} from './RepoSelectPanel'

interface AttachmentMenuProps extends UseInsertAgentProps {
  panel: AttachmentMenuPanelType | null
  onPanelChange: React.Dispatch<React.SetStateAction<AttachmentMenuPanelType | null>>
  anchorRef: RefObject<HTMLButtonElement>
}

export function AttachmentMenu({panel, onPanelChange: setPanel, anchorRef, ...props}: AttachmentMenuProps) {
  const {inputRef} = props
  const manager = useChatManager()
  const state = useChatState()
  const autocomplete = useChatAutocomplete()

  const referencesEnabled = isRepository(state.currentTopic)
  const shouldShowKnowledge =
    state.renderKnowledgeBases && ((state.currentTopic && isRepository(state.currentTopic)) || !state.currentTopic)

  // For now, we are focused on building out image support in immersive mode only, and only for supported models.
  const {model} = useChatStateValues('model')
  const shouldShowImages =
    (copilotFeatureFlags.attachImagesImmersive && state.mode === 'immersive' && !!model.capabilities.supports.vision) ||
    false

  const insertAgent = useInsertAgent(props)

  const imagesInputRef = useRef<HTMLInputElement>(null)
  const hiddenImageRef = useRef<HTMLImageElement | null>(null)

  return (
    <div>
      {shouldShowImages && (
        <>
          <CopilotImageHiddenComponent ref={hiddenImageRef} />
          <input
            id="image-uploader"
            hidden
            ref={imagesInputRef}
            type="file"
            accept={CopilotImageAttacher.getAllowedImageFileExtensions(model)}
            onChange={e => {
              if (!e.target.files) return
              const file = e.target.files[0]
              // We want to make sure this onChange handler is fired every time a selection is made, even if the
              // same file is selected twice in a row.
              e.currentTarget.value = ''
              if (!file) return
              const attacher = new CopilotImageAttacher(state, manager, autocomplete)
              void attacher.attachImageToChatInput(file, hiddenImageRef.current)
            }}
          />
        </>
      )}
      <ActionMenu
        anchorRef={anchorRef}
        open={panel === 'attachment-types'}
        onOpenChange={newOpen =>
          setPanel(currentOpen => {
            // The menu should only be able to close the menu, not the other panel types. Otherwise it will autoclose
            // the dialogs as soon as they open as it tries to close itself.
            if (newOpen && currentOpen === null) return 'attachment-types'
            if (!newOpen && currentOpen === 'attachment-types') return null
            return currentOpen
          })
        }
      >
        <ActionMenu.Overlay
          width="small"
          side="outside-top"
          align="end"
          portalContainerName={state.mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
        >
          <ActionList>
            <ActionList.Item
              onSelect={() => {
                sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_REFERENCES', mode: state.mode})
                setPanel('references')
              }}
              inactiveText={referencesEnabled ? undefined : 'First attach a repository'}
            >
              <ActionList.LeadingVisual>
                <FileCodeIcon />
              </ActionList.LeadingVisual>
              Files, folders, and symbols&hellip;
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => {
                sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_REPOSITORIES', mode: state.mode})
                setPanel('repositories')
              }}
            >
              <ActionList.LeadingVisual>
                <RepoIcon />
              </ActionList.LeadingVisual>
              Repository&hellip;
            </ActionList.Item>
            {shouldShowKnowledge && (
              <ActionList.Item
                onSelect={() => {
                  sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_KNOWLEDGE_BASES', mode: state.mode})
                  setPanel('knowledge-bases')
                }}
              >
                <ActionList.LeadingVisual>
                  <BookIcon />
                </ActionList.LeadingVisual>
                Knowledge base&hellip;
              </ActionList.Item>
            )}
            {shouldShowImages && (
              <ActionList.Item
                onSelect={() => {
                  sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_IMAGES', mode: state.mode})
                  // ensure panel is closed before file input is open
                  setPanel(null)
                  imagesInputRef.current?.click()
                }}
              >
                <ActionList.LeadingVisual>
                  <ImageIcon />
                </ActionList.LeadingVisual>
                Image&hellip;
              </ActionList.Item>
            )}
            <AgentMenu
              onSelectPanel={value => setPanel(value)}
              // setTimeout required to pull focus after ActionMenu tries to return to anchor
              onSelectAgent={agent => setTimeout(() => insertAgent(agent.slug))}
            />
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      <ReferencesSelectPanel
        open={panel === 'references'}
        onOpenChange={val => setPanel(val ? 'references' : null)}
        submitReturnFocusRef={inputRef}
        cancelReturnFocusRef={anchorRef}
      />

      <RepoSelectPanel
        open={panel === 'repositories'}
        onOpenChange={isOpen => setPanel(isOpen ? 'repositories' : null)}
        submitReturnFocusRef={inputRef}
        cancelReturnFocusRef={anchorRef}
      />

      <KnowledgeSelectPanel
        disabledForPreviewModelsProps={{anchorRef}}
        emptyKnowledgeBaseProps={{anchorRef}}
        open={panel === 'knowledge-bases'}
        onOpenChange={val => setPanel(val ? 'knowledge-bases' : null)}
        submitReturnFocusRef={inputRef}
        cancelReturnFocusRef={anchorRef}
      />

      {panel === 'no-agents-available' && <NoAgentsAvailableDialog onClose={() => setPanel(null)} />}
      {panel === 'agents-not-supported' && <AgentsNotSupportedDialog onClose={() => setPanel(null)} />}
    </div>
  )
}

try{ AttachmentMenu.displayName ||= 'AttachmentMenu' } catch {}